/**
 * ShareModal — send an estimate to retailer / installer LOCATIONS picked on the map.
 *
 * Three steps inside the action-button modal shell (static backdrop: the map is dragged):
 *   1. Pick — Retailers | Installers tabs, each an admin-configured map (the
 *      share_retailers / share_installers placements) in selectable mode, with a
 *      sticky tray of what's been picked.
 *   2. Details — who is sending (name + email, prefilled when known), optional note.
 *   3. Sent — one row per location with the outcome.
 *
 * Guests are challenged with Turnstile; a logged-in owner goes through the CSRF path.
 * Nothing here emails anyone: the server posts the marketing form handler (or, in
 * testing, the SMTP fallback) and reports back.
 */

import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Check, X, AlertTriangle, ChevronRight, ChevronLeft, Send, Store, HardHat, Info } from 'lucide-react';
import { ActionButtonModal } from '../results/ActionButtonModal';
import { TURNSTILE_CONFIG } from '../../config/integrations';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useTurnstile } from '../../hooks/useTurnstile';
import { getShareMapIds, getLoadedRetailerId } from '../../data/productTable';
import { activeLocale } from '../../i18n/standalone';
import { createShares } from '../../services/shareApi';
import type { NormalizedLocation } from '../../config/locationMaps';
import type { MapDatasetName, ShareRecipient, ShareSendResult } from '../../types/share';
import './ShareModal.css';

const LocationMap = lazy(() => import('../maps/LocationMap'));

const MESSAGE_MAX = 1000;
const MAX_PER_POST = 10;

interface Props {
  estimateGuid: string;
  prefill: { name: string; email: string };
  /** Active shares already sent — those locations read "Already sent". */
  existing: ShareRecipient[];
  /** Logged-in owner (CSRF path) vs guest (Turnstile). */
  authed: boolean;
  retailerId?: number | null;
  onClose: () => void;
  onSent: (results: ShareSendResult[]) => void;
}

type Step = 'pick' | 'details' | 'done';

const keyOf = (dataset: MapDatasetName, loc: NormalizedLocation) => `${dataset}:${loc.id}`;

export const ShareModal: React.FC<Props> = ({ estimateGuid, prefill, existing, authed, retailerId, onClose, onSent }) => {
  const { t } = useTranslation('results');
  const { settings: appSettings } = useAppSettings();
  const mapIds = getShareMapIds();
  const tabs = useMemo(() => {
    const out: { dataset: MapDatasetName; mapId: number }[] = [];
    if (mapIds.retailers) out.push({ dataset: 'retailers', mapId: mapIds.retailers });
    if (mapIds.installers) out.push({ dataset: 'installers', mapId: mapIds.installers });
    return out;
  }, [mapIds.retailers, mapIds.installers]);

  const [step, setStep] = useState<Step>('pick');
  const [dataset, setDataset] = useState<MapDatasetName>(tabs[0]?.dataset ?? 'retailers');
  const [picked, setPicked] = useState<Map<string, { dataset: MapDatasetName; loc: NormalizedLocation }>>(new Map());
  const [senderName, setSenderName] = useState(prefill.name);
  const [senderEmail, setSenderEmail] = useState(prefill.email);
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ShareSendResult[]>([]);

  // Viewport-fitted map height (same arithmetic as MapModal, minus the tray).
  const fit = () => Math.max(300, Math.min(900, window.innerHeight - 300));
  const [height, setHeight] = useState(fit);
  useEffect(() => {
    const onResize = () => setHeight(fit());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Turnstile only for guests, only on the details step.
  const turnstileActive = !authed && TURNSTILE_CONFIG.enabled && appSettings.turnstileEnabled;
  const turnstileRef = useRef<HTMLDivElement>(null);
  const { token: turnstileToken, reset: resetTurnstile } = useTurnstile(turnstileActive, turnstileRef, step === 'details');

  // Locations already shared (active) can't be picked again; they get "Already sent".
  const alreadySent = useMemo(() => {
    const s = new Set<string>();
    for (const r of existing) {
      if (r.status !== 'active') continue;
      if (r.sfid) s.add(r.sfid);
      if (r.publicId) s.add(r.publicId);
    }
    return s;
  }, [existing]);

  const selectedIdsFor = useCallback((ds: MapDatasetName) => {
    const s = new Set<string>();
    picked.forEach((v, k) => { if (v.dataset === ds) s.add(v.loc.id); void k; });
    return s;
  }, [picked]);

  const onSelect = useCallback((loc: NormalizedLocation) => {
    setPicked(prev => {
      const next = new Map(prev);
      const k = keyOf(dataset, loc);
      if (next.has(k)) next.delete(k);
      else if (next.size < MAX_PER_POST) next.set(k, { dataset, loc });
      return next;
    });
  }, [dataset]);

  // One stable object per (dataset, selection) — LocationMap re-renders its list on identity.
  const selectedIds = useMemo(() => selectedIdsFor(dataset), [selectedIdsFor, dataset]);
  const selectableOpts = useMemo(() => ({
    onSelect,
    selectedIds,
    disabledIds: alreadySent,
    label: t('share.map.select'),
    selectedLabel: t('share.map.selected'),
    disabledLabel: t('share.map.alreadySent'),
  }), [onSelect, selectedIds, alreadySent, t]);

  const removePick = (k: string) => setPicked(prev => { const n = new Map(prev); n.delete(k); return n; });

  const pickedList = useMemo(() => Array.from(picked.entries()), [picked]);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderEmail.trim());

  const submit = async () => {
    setError(null);
    if (senderName.trim() === '') { setError(t('share.modal.errors.name')); return; }
    if (!emailOk) { setError(t('share.modal.errors.email')); return; }
    if (pickedList.length === 0) { setError(t('share.modal.errors.none')); return; }
    if (turnstileActive && !turnstileToken) { setError(t('share.modal.errors.challenge')); return; }
    setSubmitting(true);
    try {
      const res = await createShares({
        guid: estimateGuid,
        senderName: senderName.trim(),
        senderEmail: senderEmail.trim(),
        message: message.trim() || undefined,
        locale: activeLocale(),
        locations: pickedList.map(([, v]) => ({ sfid: v.loc.sfid || v.loc.id, dataset: v.dataset, id: v.loc.id })),
        turnstileToken: turnstileActive ? turnstileToken : undefined,
        website: honeypot,
      }, authed);
      setResults(res);
      setStep('done');
      onSent(res);
    } catch (e) {
      const err = e as Error & { status?: number };
      setError(err.status === 429 ? t('share.modal.errors.tooMany') : (err.message || t('share.modal.errors.failed')));
      resetTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  const title = step === 'pick' ? t('share.modal.title') : step === 'details' ? t('share.modal.detailsTitle') : t('share.modal.doneTitle');
  const activeTab = tabs.find(x => x.dataset === dataset) ?? tabs[0];

  return (
    <ActionButtonModal title={title} contentHtml="" size="xl" onClose={onClose} bodyClassName="p-0" staticBackdrop>
      <div className="estshare">
        {/* ---- Step 1: pick locations ---- */}
        {step === 'pick' && (
          <>
            {tabs.length === 0 ? (
              <div className="estshare__empty">
                <AlertTriangle size={20} /> {t('share.modal.noMaps')}
              </div>
            ) : (
              <>
                {tabs.length > 1 && (
                  <ul className="nav nav-tabs estshare__tabs" role="tablist">
                    {tabs.map(tab => (
                      <li className="nav-item" role="presentation" key={tab.dataset}>
                        <button type="button" role="tab" aria-selected={dataset === tab.dataset}
                          className={`nav-link d-inline-flex align-items-center gap-2 ${dataset === tab.dataset ? 'active' : ''}`}
                          onClick={() => setDataset(tab.dataset)}>
                          {tab.dataset === 'retailers' ? <Store size={15} /> : <HardHat size={15} />}
                          {tab.dataset === 'retailers' ? t('share.modal.tabs.retailers') : t('share.modal.tabs.installers')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="estshare__hint"><Info size={14} /> {t('share.modal.pickHint', { max: MAX_PER_POST })}</p>
                <div className="estshare__map">
                  {activeTab && (
                    <Suspense fallback={<div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}><Loader2 size={22} className="animate-spin" /></div>}>
                      <LocationMap
                        key={activeTab.dataset}
                        mapId={activeTab.mapId}
                        context="modal"
                        estimateGuid={estimateGuid}
                        retailerId={retailerId ?? getLoadedRetailerId()}
                        heightPx={height}
                        selectable={selectableOpts}
                      />
                    </Suspense>
                  )}
                </div>
              </>
            )}
            <div className="estshare__tray">
              <div className="estshare__tray-chips">
                {pickedList.map(([k, v]) => (
                    <span className="estshare-chip" key={k}>
                      {v.dataset === 'retailers' ? <Store size={12} /> : <HardHat size={12} />}
                      <span className="estshare-chip__name">{v.loc.name}</span>
                      {v.loc.city && <span className="estshare-chip__city">{v.loc.city}</span>}
                      <button type="button" className="estshare-chip__x" aria-label={t('share.modal.removePick', { name: v.loc.name })} onClick={() => removePick(k)}><X size={12} /></button>
                    </span>
                ))}
              </div>
              <div className="estshare__tray-actions">
                <span className="small text-muted" aria-live="polite">{t('share.modal.selectedCount', { count: pickedList.length })}</span>
                <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" disabled={pickedList.length === 0} onClick={() => { setError(null); setStep('details'); }}>
                  {t('share.modal.continue')} <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* ---- Step 2: sender + note ---- */}
        {step === 'details' && (
          <form className="estshare__form" onSubmit={e => { e.preventDefault(); void submit(); }}>
            <div className="estshare__recipients">
              <div className="fw-semibold mb-1">{t('share.modal.sendingTo', { count: pickedList.length })}</div>
              <ul className="estshare__recipient-list">
                {pickedList.map(([k, v]) => (
                  <li key={k}>
                    {v.dataset === 'retailers' ? <Store size={13} /> : <HardHat size={13} />}
                    <span>{v.loc.name}</span>
                    {(v.loc.city || v.loc.region) && <span className="text-muted">— {[v.loc.city, v.loc.region].filter(Boolean).join(', ')}</span>}
                  </li>
                ))}
              </ul>
            </div>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label htmlFor="share-sender-name" className="form-label fw-semibold">{t('share.modal.senderName')}</label>
                <input id="share-sender-name" className="form-control" value={senderName} maxLength={120} autoComplete="name" required
                  onChange={e => setSenderName(e.target.value)} disabled={submitting} />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="share-sender-email" className="form-label fw-semibold">{t('share.modal.senderEmail')}</label>
                <input id="share-sender-email" type="email" className="form-control" value={senderEmail} autoComplete="email" required
                  onChange={e => setSenderEmail(e.target.value)} disabled={submitting} />
              </div>
              <div className="col-12">
                <label htmlFor="share-message" className="form-label fw-semibold">
                  {t('share.modal.message')} <span className="text-muted fw-normal">{t('share.modal.messageOptional')}</span>
                </label>
                <textarea id="share-message" className="form-control" rows={3} maxLength={MESSAGE_MAX} value={message}
                  placeholder={t('share.modal.messagePlaceholder')} onChange={e => setMessage(e.target.value)} disabled={submitting} />
                <div className="form-text text-end">{message.length}/{MESSAGE_MAX}</div>
              </div>
            </div>
            {/* Honeypot (hidden from real users) */}
            <div className="estshare-hp" aria-hidden="true" tabIndex={-1}>
              <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
            </div>
            <p className="estshare__privacy"><Info size={14} /> {t('share.modal.privacyNote')}</p>
            {turnstileActive && <div className="estshare__turnstile"><div ref={turnstileRef} /></div>}
            {error && <div className="alert alert-danger py-2 small mb-3" role="alert">{error}</div>}
            <div className="estshare__form-actions">
              <button type="button" className="btn btn-outline-secondary d-inline-flex align-items-center gap-1" disabled={submitting} onClick={() => setStep('pick')}>
                <ChevronLeft size={16} /> {t('share.modal.back')}
              </button>
              <button type="submit" className="btn btn-primary d-inline-flex align-items-center gap-2" disabled={submitting || (turnstileActive && !turnstileToken)}>
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? t('share.modal.sending') : t('share.modal.send', { count: pickedList.length })}
              </button>
            </div>
          </form>
        )}

        {/* ---- Step 3: results ---- */}
        {step === 'done' && (
          <div className="estshare__form">
            <ul className="estshare__results">
              {results.map((r, i) => {
                const ok = r.status === 'sent';
                const exists = r.status === 'exists';
                const saved = r.status === 'saved_not_sent';
                return (
                  <li key={`${r.sfid}-${i}`} className={`estshare-result estshare-result--${r.status}`}>
                    <span className="estshare-result__icon">{ok || exists ? <Check size={16} /> : saved ? <AlertTriangle size={16} /> : <X size={16} />}</span>
                    <span className="estshare-result__name">{r.locationName || r.sfid}</span>
                    <span className="estshare-result__status">
                      {ok && (r.redirected ? t('share.modal.result.sentRedirected') : t('share.modal.result.sent'))}
                      {exists && t('share.modal.result.exists')}
                      {saved && t('share.modal.result.savedNotSent')}
                      {r.status === 'failed' && t('share.modal.result.failed')}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="estshare__privacy"><Info size={14} /> {t('share.modal.doneHint')}</p>
            <div className="estshare__form-actions justify-content-end">
              <button type="button" className="btn btn-primary" onClick={onClose}>{t('share.modal.done')}</button>
            </div>
          </div>
        )}
      </div>
    </ActionButtonModal>
  );
};

export default ShareModal;
